import { type QueryKey, useInfiniteQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { get } from "common/helpers/HTTP";

import type { InfiniteData } from "@tanstack/query-core";
import type { ConversationMessage } from "../types";

const itemsPerPage = 80;
type PagingParams = { before: number | "now" } | { after: number | "now" };

async function getMessages(
  workspaceId: string,
  accountId: string,
  pagingParams: PagingParams,
  conversationUrn: string,
): Promise<ConversationMessage[]> {
  const searchParams = new URLSearchParams();
  searchParams.append("count", itemsPerPage.toString());
  if ("before" in pagingParams) {
    searchParams.append(
      "created_before",
      (pagingParams.before === "now"
        ? Date.now()
        : pagingParams.before
      ).toString(),
    );
  } else {
    searchParams.append(
      "created_after",
      (pagingParams.after === "now"
        ? Date.now()
        : pagingParams.after
      ).toString(),
    );
  }

  const { messages } = await get(
    `workspaces/${workspaceId}/accounts/${accountId}/conversations/${conversationUrn}?${searchParams.toString()}`,
  );
  if ("after" in pagingParams) {
    return messages.toReversed();
  }
  return messages;
}
export default function useConversationMessages(
  params:
    | {
        accountId: string;
        conversationUrn: string;
        unreadCount: number;
        lastActivityAt: number | undefined;
      }
    | undefined,
) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { conversationsKeys } = getQueryKeys(workspaceId);

  const {
    isFetchingNextPage: isLoadingNext,
    isFetchingPreviousPage: isLoadingPrevious,
    isFetching: isLoading,
    data: messages,
    fetchNextPage: loadNextMessages,
    fetchPreviousPage: loadPreviousMessages,
    hasNextPage: hasNextMessages,
    hasPreviousPage: hasPreviousMessages,
    refetch,
  } = useInfiniteQuery<
    ConversationMessage[],
    unknown,
    InfiniteData<ConversationMessage[], PagingParams>,
    QueryKey,
    PagingParams
  >({
    queryKey: params
      ? conversationsKeys.messages(
          params.conversationUrn,
          params.accountId,
          params.lastActivityAt,
        )
      : undefined,
    queryFn: params
      ? ({ pageParam }) =>
          getMessages(
            workspaceId,
            params.accountId,
            pageParam,
            params.conversationUrn,
          )
      : undefined,
    enabled: !!params,
    initialPageParam: params && {
      before: params.lastActivityAt ? params.lastActivityAt + 1 : "now",
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length) {
        return { before: lastPage[lastPage.length - 1].created_at };
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.length) {
        return { after: firstPage[0].created_at };
      }
      return undefined;
    },
    maxPages: 6,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (messages && params && params.unreadCount && !params.lastActivityAt) {
      refetch();
    }
  }, [params]);

  async function loadMessages(param: "next" | "prev") {
    if (param === "next") {
      await loadNextMessages();
    } else {
      await loadPreviousMessages();
    }
  }

  return {
    messages,
    loadMessages,
    isLoadingMessages: {
      next: isLoadingNext,
      prev: isLoadingPrevious,
      all: isLoading,
    },
    hasMoreMessages: { next: hasNextMessages, prev: hasPreviousMessages },
  };
}
